import React, { useEffect, useState } from "react";
import { skipGreeting } from "../textPreview";
import { Link } from "react-router-dom";
import { Newspaper, GraduationCap, Stethoscope, CalendarDays, ArrowRight, Clock, Repeat, Sparkles, Wand2 } from "lucide-react";
import { API_URL } from "../config";
import { useAuth } from "../AuthContext";
import { useSpecialty, SPECIALTIES } from "../SpecialtyContext";
import PopularWidget from "./PopularWidget";
import TopAuthorsWidget from "./TopAuthorsWidget";
import SpecialtyEditorBanner from "./SpecialtyEditorBanner";
import BannerSlider from "./BannerSlider";
import SeriesStrip from "./SeriesStrip";
import ContinueWatching from "./ContinueWatching";

const KIND_PATH = { post: "articles", case: "cases", lecture: "lectures" };

function RecommendedWidget({ token }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!token) { setItems([]); return; }
    fetch(`${API_URL}/recommendations/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setItems)
      .catch(() => setItems([]));
  }, [token]);

  // Строится по истории запросов в умном поиске — если запросов ещё не было,
  // рекомендовать нечего, секцию просто не показываем (не пустую заглушку).
  if (!token || items.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 20, background: "#fff1f6", border: "1.5px solid #ffc2d9" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, color: "#e5005b", fontSize: "0.95rem", marginBottom: 10 }}>
        <Wand2 size={16} strokeWidth={2.2} /> Рекомендовано для вас
      </div>
      <p style={{ color: "#b0476f", fontSize: "0.82rem", margin: "-4px 0 10px" }}>
        На основе ваших недавних запросов в умном поиске
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map(item => (
          <Link key={`${item.kind}_${item.id}`} to={`/${KIND_PATH[item.kind]}/${item.id}`}
            style={{ display: "block", padding: "8px 10px", borderRadius: 8, textDecoration: "none", background: "#fff" }}>
            <span style={{ color: "#007c84", fontWeight: 600, fontSize: "0.92rem" }}>{item.title}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const SECTIONS = [
  { key: "articles", title: "Свежие статьи", to: "/articles", icon: Newspaper, endpoint: "/posts", color: "#007c84" },
  { key: "lectures", title: "Новые материалы", to: "/lectures", icon: GraduationCap, endpoint: "/lectures", color: "#15803d" },
  { key: "cases", title: "Клинические случаи", to: "/cases", icon: Stethoscope, endpoint: "/cases", color: "#e5005b" },
];

// Видео с обложкой — вперёд (сохраняя порядок «новое сверху»), файлы без обложки — в конец
function withCoverFirst(list) {
  if (!Array.isArray(list)) return [];
  return [...list.filter(x => x.thumbnail_url), ...list.filter(x => !x.thumbnail_url)];
}

function preview(text, len = 140) {
  if (!text) return "";
  const plain = text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_`>\-[\]]/g, "")
    .trim();
  const shown = skipGreeting(plain);
  return shown.length > len ? shown.slice(0, len) + "…" : shown;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ru", { day: "numeric", month: "short" }); } catch { return ""; }
}

function specialtyLabel(id) {
  return SPECIALTIES.find(s => s.id === id)?.label || id;
}

function SectionBlock({ section, items, loading, onSpecialtyClick }) {
  const Icon = section.icon;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: section.color, fontSize: "1rem" }}>
          <Icon size={18} strokeWidth={2.2} /> {section.title}
        </div>
        <Link to={section.to} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.85rem", color: "#888", textDecoration: "none" }}>
          Все <ArrowRight size={14} strokeWidth={2} />
        </Link>
      </div>

      {loading && <p style={{ color: "#aaa", fontSize: "0.9rem" }}>Загрузка…</p>}
      {!loading && items.length === 0 && (
        <p style={{ color: "#aaa", fontSize: "0.9rem", fontStyle: "italic" }}>Пока пусто.</p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map(item => (
          <div key={item.id} className={item.thumbnail_url ? "home-row-with-cover" : undefined} style={{ borderBottom: "1px solid #eef0f6", paddingBottom: 10 }}>
            {item.thumbnail_url && (
              <Link to={`${section.to}/${item.id}`} className="home-row-cover" aria-hidden="true" tabIndex={-1}>
                <img src={item.thumbnail_url} alt="" loading="lazy" />
              </Link>
            )}
            <div className="home-row-body">
            <Link to={`${section.to}/${item.id}`} style={{ textDecoration: "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                <span style={{ fontWeight: 600, color: "#222", fontSize: "0.96rem" }}>{item.title}</span>
              </div>
            </Link>
            <div style={{ display: "flex", alignItems: "center", gap: "4px 10px", flexWrap: "wrap", fontSize: "0.8rem", color: "#999" }}>
              {item.specialty && (
                <span
                  className="spec-badge"
                  style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}
                  title="Нажмите, чтобы переключить специальность на этот раздел"
                  onClick={() => onSpecialtyClick(item.specialty)}
                >
                  <Repeat size={10} strokeWidth={2.4} />
                  {specialtyLabel(item.specialty)}
                </span>
              )}
              {item.author && <span>{item.author}</span>}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                <Clock size={12} strokeWidth={2} /> {fmtDate(item.created_at)}
              </span>
            </div>
            {(item.content || item.description) && (
              <Link to={`${section.to}/${item.id}`} style={{ textDecoration: "none" }}>
                <div style={{ color: "#777", fontSize: "0.88rem", marginTop: 4, lineHeight: 1.4 }}>
                  {preview(item.content || item.description)}
                </div>
              </Link>
            )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home({ onLoginRequest }) {
  const { token } = useAuth();
  const { specialty, setSpecialty } = useSpecialty();
  const [data, setData] = useState({ articles: [], lectures: [], cases: [] });
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);

  // AI-дайджест недели по выбранной специальности (генерируется раз в неделю
  // scripts/digest_agent.py на основе свежих публикаций и активности в чате).
  // Без выбранной специальности («весь портал») дайджест не привязан ни к
  // одному разделу — не показываем.
  useEffect(() => {
    if (!specialty?.id) { setDigest(null); return; }
    fetch(`${API_URL}/digest/latest?specialty=${specialty.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(setDigest)
      .catch(() => setDigest(null));
  }, [specialty]);

  // Выбрана специальность — лента только по ней; «Весь портал» — сводная
  // лента по всем специальностям сразу (прежнее единственное поведение).
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 5 });
    if (specialty?.id) params.set("specialty", specialty.id);
    Promise.all(SECTIONS.map(s => {
      const q = new URLSearchParams(params);
      if (s.key === "lectures") q.set("limit", 15);
      return fetch(`${API_URL}${s.endpoint}?${q}`).then(r => r.ok ? r.json() : []);
    }))
      .then(([articles, lectures, cases]) => setData({ articles, lectures: withCoverFirst(lectures).slice(0, 5), cases }))
      .catch(() => setData({ articles: [], lectures: [], cases: [] }))
      .finally(() => setLoading(false));
  }, [specialty]);

  const goToSpecialty = id => {
    const s = SPECIALTIES.find(x => x.id === id);
    if (s) setSpecialty(s);
  };

  return (
    <section>
      <BannerSlider
        specialtyId={specialty?.id}
        hero={(
      <div
          className={specialty ? undefined : "home-hero-welcome"}
          style={specialty
            ? { background: "linear-gradient(135deg, #007c84 0%, #009ba4 100%)", borderRadius: 16, padding: "32px 28px 40px", color: "#fff" }
            : { "--hero-img": "url(/hero-welcome.webp)", "--hero-img-m": "url(/hero-welcome-m.webp)" }}
        >
          {!specialty && <div className="home-hero-pic" aria-hidden="true" />}
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 8px", padding: 0, border: "none", color: "inherit" }}>
            {specialty ? specialty.label : "Добро пожаловать"}
          </h1>
          <div style={{ fontSize: "0.95rem", opacity: 0.9, maxWidth: 640, lineHeight: 1.5 }}>
            {specialty
              ? "Свежее по этому разделу: статьи, материалы и разбор клинических случаев. Переключите специальность в меню слева или выберите «Весь портал» для общей ленты."
              : "Здесь собраны самые свежие статьи, обучающие материалы и разбор клинических случаев по всем специальностям. Нажмите на бейдж специальности у публикации или выберите её в меню слева, чтобы смотреть контент прицельно."}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
            <Link to="/articles" className="header-auth-btn home-hero-btn" style={{ background: "#fff", color: "#007c84" }}>Читать статьи</Link>
            <Link to="/lectures" className="header-auth-btn home-hero-btn" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}>Смотреть материалы</Link>
            <Link to="/events" className="header-auth-btn home-hero-btn" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}>
              <CalendarDays size={14} strokeWidth={2.2} style={{ marginRight: 4 }} /> События
            </Link>
          </div>
        </div>
        )}
      />


      {/* Подборки видео — только на тематических страницах (выбрана специальность); на общей главной их нет */}
      {specialty && <SeriesStrip specialtyId={specialty.id} specialtyName={specialty.label} showEmpty />}

      <ContinueWatching />


      {specialty && (
        <SpecialtyEditorBanner
          specialty={specialty.id}
          specialtyLabel={specialty.label}
          onLoginRequest={onLoginRequest}
        />
      )}

      {digest && (
        <div className="card" style={{
          marginBottom: 20, background: "#fff1f6", border: "1.5px solid #ffc2d9",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#e5005b", fontSize: "0.95rem", marginBottom: 8 }}>
            <Sparkles size={16} strokeWidth={2.2} /> Дайджест недели · {specialty.label}
          </div>
          <div style={{ color: "#9c0040", fontSize: "0.92rem", lineHeight: 1.55, whiteSpace: "pre-line" }}>
            {digest.content}
          </div>
        </div>
      )}

      <RecommendedWidget token={token} />
      <PopularWidget global={!specialty} />
      <TopAuthorsWidget global={!specialty} />

      {SECTIONS.map(s => (
        <SectionBlock key={s.key} section={s} items={data[s.key]} loading={loading} onSpecialtyClick={goToSpecialty} />
      ))}
    </section>
  );
}
